import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Skeleton,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { colors } from 'packages/common-frontend/colors';

type BreadcrumbType = 'link' | 'text' | 'loading';

export type BreadcrumbProps =
  | {
      type?: never;
      label: string;
      path?: string;
    }
  | { type: 'loading' };

type BreadcrumbOptions =
  | { type: 'loading' }
  | { label: string; path?: string; type: Exclude<BreadcrumbType, 'loading'> };

const _getBreadcrumbType = (breadcrumb: BreadcrumbProps): BreadcrumbType => {
  if ('type' in breadcrumb) {
    return 'loading';
  }
  if (breadcrumb.path) {
    return 'link';
  }
  return 'text';
};

const _getBreadcrumb = (breadcrumb: BreadcrumbProps): BreadcrumbOptions => {
  const type = _getBreadcrumbType(breadcrumb);

  return {
    type,
    ...breadcrumb,
  };
};

const _getBreadcrumbKey = (breadcrumb: BreadcrumbOptions): string => {
  switch (breadcrumb.type) {
    case 'loading':
      return 'loading';
    case 'link':
    case 'text':
      return `${breadcrumb.type}-${breadcrumb.label}-${breadcrumb.path}`;
    default:
      return finleyAssertUnreachable(breadcrumb);
  }
};

type BreadcrumbsProps = { breadcrumbs: BreadcrumbProps[] };
export const Breadcrumbs = ({ breadcrumbs }: BreadcrumbsProps) => {
  const { pathname: currentPath, search: currentSearch } = useLocation();

  return (
    <Breadcrumb
      spacing="8px"
      separator={<ChevronRightIcon color={colors.gray[500]} />}
      fontSize="xs"
      color={colors.gray[500]}
    >
      {_.map(breadcrumbs, (_breadcrumb, index) => {
        const breadcrumb = _getBreadcrumb(_breadcrumb);
        return (
          <BreadcrumbItem
            isCurrentPage={index === breadcrumbs.length - 1}
            key={_getBreadcrumbKey(breadcrumb)}
          >
            {(() => {
              switch (breadcrumb.type) {
                case 'loading': {
                  return <Skeleton w="150px" h="20px" />;
                }
                case 'link':
                case 'text':
                  return (
                    <BreadcrumbLink
                      as={Link}
                      to={breadcrumb.path ?? `${currentPath}${currentSearch}`}
                    >
                      {breadcrumb.label}
                    </BreadcrumbLink>
                  );
                default:
                  return finleyAssertUnreachable(breadcrumb);
              }
            })()}
          </BreadcrumbItem>
        );
      })}
    </Breadcrumb>
  );
};
